import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layers, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetPasswordPage = ({ kind = "invite" }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [info, setInfo] = useState({ email: "", name: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const isInvite = kind === "invite";

  useEffect(() => {
    (async () => {
      if (!token) { setChecking(false); return; }
      try {
        const { data } = await axios.get(`${API}/auth/token-info`, { params: { token, kind: isInvite ? "invite" : "reset" } });
        setValid(!!data.valid);
        if (data.valid) setInfo({ email: data.email, name: data.name });
      } catch { setValid(false); }
      finally { setChecking(false); }
    })();
  }, [token, isInvite]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const endpoint = isInvite ? "accept-invite" : "reset-password";
      await axios.post(`${API}/auth/${endpoint}`, { token, password: pw });
      toast.success(isInvite ? "Account ready — please sign in" : "Password updated — please sign in");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="relative w-full max-w-md px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Layers className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">
              Support<span className="text-blue-600">IQ</span>
            </h1>
            <p className="text-gray-500 mt-2">{isInvite ? "Set your password" : "Choose a new password"}</p>
          </div>

          {checking ? (
            <div className="flex justify-center py-8"><Loader2 className="w-7 h-7 text-blue-600 animate-spin" /></div>
          ) : !valid ? (
            <div className="text-center space-y-4" data-testid="token-invalid">
              <p className="text-gray-600">This link is invalid or has expired.</p>
              <Button onClick={() => navigate("/login")} className="bg-blue-600 hover:bg-blue-700 text-white">Back to sign in</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <p className="text-sm text-gray-600 text-center flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-blue-600" /> {info.email}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters" className="pl-10 h-11" data-testid="new-password" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    placeholder="Re-enter password" className="pl-10 h-11" data-testid="confirm-password" />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading} data-testid="set-password-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isInvite ? "Set password" : "Update password")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
